import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Iconify from './Iconify';
import { DOMAIN } from '../redux/constants';
import { DUR } from '../utils/motion';
import { LABEL_MICRO, LABEL_SM, NUMERAL_MD, TITLE_SECTION } from '../utils/typeScale';
import { RED, YELLOW } from '../utils/brandColors';
import { compactNumber } from '../utils/formatNumber';

StreakLounge.propTypes = {
  onLocked: PropTypes.func,
  onNavigate: PropTypes.func,
};

/**
 * The Tier-2 Streak Lounge. `GET /engagement/streak/lounge/` has been live and
 * fully implemented with no UI at all — this is the surface for it.
 *
 * A 403 means the caller is below Tier 2. That is not an error state: the
 * caller (StreakModal) swaps back to the tier ladder so the user sees how to
 * unlock it rather than a failure message.
 */
export default function StreakLounge({ onLocked, onNavigate }) {
  const { userInfo } = useSelector((state) => state.userLogin);
  const navigate = useNavigate();
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const access = userInfo?.access;

  useEffect(() => {
    let isMounted = true;
    if (!access) {
      setLoading(false);
      if (onLocked) onLocked();
      return undefined;
    }
    axios
      .get(`${DOMAIN}/api/v1/engagement/streak/lounge/`, {
        headers: { Authorization: `Bearer ${access}` },
      })
      .then((res) => {
        if (isMounted) setQuestions(res.data?.questions || []);
      })
      .catch((err) => {
        if (!isMounted) return;
        // 403 = below Tier 2. Hand back to the ladder rather than showing an error.
        if (err?.response?.status === 403 && onLocked) onLocked();
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
    // onLocked deliberately omitted: callers pass inline arrows (fresh
    // identity per render), and StreakModal re-renders every second during
    // a recovery countdown - keeping it in deps re-fired this fetch on
    // every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access]);

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={22} sx={{ color: RED }} />
      </Stack>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <Typography sx={{ ...LABEL_SM, py: 3, textAlign: 'center' }}>
        No lounge picks right now. Check back after the next round of questions lands.
      </Typography>
    );
  }

  const go = (id) => {
    if (onNavigate) onNavigate();
    navigate(`/v/${id}`);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Iconify icon="eva:star-outline" width={18} height={18} sx={{ color: RED }} />
        <Typography variant="h6" sx={TITLE_SECTION}>
          Streak Lounge
        </Typography>
      </Stack>
      <Typography sx={{ ...LABEL_SM, mb: 2 }}>
        The most-voted questions on the app, for Tier 2 and above.
      </Typography>

      <Stack>
        {questions.map((q) => (
          <ButtonBase
            key={q.id}
            onClick={() => go(q.id)}
            sx={{
              display: 'grid',
              gridTemplateColumns: '44px 1fr auto',
              alignItems: 'center',
              gap: 1.5,
              width: '100%',
              textAlign: 'left',
              px: 1,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              transition: `background-color ${DUR.micro}ms ease`,
              '&:hover': { bgcolor: 'action.hover' },
              '&:active': { transform: 'scale(0.995)' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                height: 44,
                borderRadius: '8px',
                overflow: 'hidden',
                bgcolor: 'action.hover',
              }}
            >
              <Box sx={{ width: '50%', bgcolor: RED }} />
              <Box sx={{ width: '50%', bgcolor: YELLOW }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {q.title || `${q.option_a_label} or ${q.option_b_label}`}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={NUMERAL_MD}>{compactNumber(q.total_votes || 0)}</Typography>
              <Typography sx={LABEL_MICRO}>Votes</Typography>
            </Box>
          </ButtonBase>
        ))}
      </Stack>
    </Box>
  );
}
